import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {FormControlModule} from '../../../../../pipes/form-control/form-control.module';
import {NgClass, NgForOf, NgIf} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {ToastrService} from 'ngx-toastr';
import {FormControlErrorMessagesModule} from '../../../../../pipes/form-control-error-messages/form-control-error-messages.module';
import {LoaderComponent} from '../../../../../shared-components/loader/loader.component';
import {CdkDragDrop, DragDropModule, moveItemInArray} from '@angular/cdk/drag-drop';
import {InvitationsHttpService} from '../../../../../services/http-services/invitations/invitations-http.service';

type InvitationMediaType = 'image' | 'video';

interface InvitationMediaItem {
  id?: number;
  name: string;
  fullPath?: string;
  posterPath?: string;
  type: InvitationMediaType;
  gender?: 'boys' | 'girls';
  age?: number;
  position: number;
  file?: File;
}

@Component({
  selector: 'gr-edit-invitation-modal',
  templateUrl: './edit-invitation-modal.component.html',
  imports: [
    FormControlModule,
    NgIf,
    NgClass,
    ReactiveFormsModule,
    FormControlErrorMessagesModule,
    NgForOf,
    LoaderComponent,
    DragDropModule
  ],
  standalone: true
})
export class EditInvitationModalComponent implements OnInit {
  formTriedToSubmit = false;
  editInvitationForm!: FormGroup;
  mediaItemsForEdit: InvitationMediaItem[] = [];
  deletedMediaIds: number[] = [];
  previewVideoItem: InvitationMediaItem | null = null;
  isLoaderActive = false;

  @Input() mediaType: InvitationMediaType = 'image';
  @Input() mediaItems: InvitationMediaItem[] = [];
  @Input() invitationId: number | null = null;
  @Output() closeModal: EventEmitter<void> = new EventEmitter<void>();
  @Output() confirmModal: EventEmitter<any> = new EventEmitter<any>();
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  readonly imageMaxSize = 10485760;
  readonly videoMaxSize = 104857600;

  constructor(private formBuilder: FormBuilder,
              private toastService: ToastrService,
              private invitationsHttpService: InvitationsHttpService) {
  }

  ngOnInit(): void {
    this.buildForm();
  }

  get isVideoMode(): boolean {
    return this.mediaType === 'video';
  }

  get modalTitle(): string {
    return this.isVideoMode ? 'Izmeni video za pozivnicu' : 'Izmeni slike za pozivnicu';
  }

  get galleryTitle(): string {
    return this.isVideoMode ? 'Dizajn video klipova' : 'Dizajn pozivnica';
  }

  get addButtonLabel(): string {
    return this.isVideoMode ? 'Dodaj video' : 'Dodaj slike';
  }

  get deleteAllLabel(): string {
    return this.isVideoMode ? 'Obrisi sve video klipove' : 'Obrisi sve slike';
  }

  get emptyMediaMessage(): string {
    return this.isVideoMode ? 'Molimo vas dodajte video klip.' : 'Molimo vas dodajte sliku.';
  }

  get acceptedFileTypes(): string {
    return this.isVideoMode ? '.mp4,.webm,.mov' : '.jpg,.jpeg,.png,.webp';
  }

  buildForm(): void {
    this.editInvitationForm = this.formBuilder.group({
      media: [[]],
      deleted_images_ids: [[]],
      deleted_videos_ids: [[]]
    });
    this.mediaItemsForEdit = this.mediaItems.map((item, index) => ({
      ...item,
      position: index + 1
    }));
    this.editInvitationForm.controls.media.patchValue(this.mediaItemsForEdit.length ? this.mediaItemsForEdit : null);
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const selectedFiles = Array.from(input.files);
      selectedFiles.forEach(file => {
        if (!this.isAllowedFileType(file)) {
          this.toastService.warning(this.isVideoMode
            ? 'Pogresan tip dokumenta. Samo MP4, WEBM i MOV formati su dozvoljeni.'
            : 'Pogresan tip dokumenta. Samo JPG, JPEG, PNG i WEBP formati su dozvoljeni.');
          return;
        }

        if (file.size > this.currentMaxSize()) {
          this.toastService.warning(`${file.name} je veci od ${this.isVideoMode ? '100MB' : '10MB'}.`);
          return;
        }

        this.mediaItemsForEdit.push({
          fullPath: URL.createObjectURL(file),
          id: undefined,
          file,
          name: this.getFileNameWithoutExtension(file.name),
          type: this.mediaType,
          position: this.mediaItemsForEdit.length + 1
        });
      });
      this.patchMediaControl();
      if (this.fileInput) {
        this.fileInput.nativeElement.value = '';
      }
    }
  }

  removeAllMedia(): void {
    this.mediaItemsForEdit.forEach((item: InvitationMediaItem) => {
      if (item.id) {
        this.addDeletedMediaId(item.id);
      }
    });
    this.mediaItemsForEdit = [];
    this.patchDeletedMediaControl();
    this.patchMediaControl();
  }

  removeSingleMedia(mediaItem: InvitationMediaItem): void {
    if (this.previewVideoItem?.fullPath === mediaItem.fullPath && this.previewVideoItem?.id === mediaItem.id) {
      this.closeVideoPreview();
    }

    const itemIndex = this.mediaItemsForEdit.findIndex((item: InvitationMediaItem) => {
      return item.fullPath === mediaItem.fullPath && item.id === mediaItem.id;
    });
    if (itemIndex !== -1) {
      if (mediaItem.id) {
        this.addDeletedMediaId(mediaItem.id);
      }
      this.mediaItemsForEdit.splice(itemIndex, 1);
      this.resetPositions();
      this.patchDeletedMediaControl();
      this.patchMediaControl();
    }
  }

  dropMediaItem(event: CdkDragDrop<InvitationMediaItem[]>): void {
    moveItemInArray(this.mediaItemsForEdit, event.previousIndex, event.currentIndex);
    this.resetPositions();
    this.patchMediaControl();
  }

  openVideoPreview(mediaItem: InvitationMediaItem): void {
    if (mediaItem.type !== 'video' || !mediaItem.fullPath) return;

    this.previewVideoItem = mediaItem;
  }

  closeVideoPreview(): void {
    this.previewVideoItem = null;
  }

  onSubmitEditInvitationForm(): void {
    this.formTriedToSubmit = true;
    if (!this.invitationId) {
      this.toastService.error('Nije pronadjena pozivnica za izabrani filter.');
      return;
    }

    this.isLoaderActive = true;
    this.resetPositions();
    const formData = this.buildFormData();
    this.invitationsHttpService.putInvitation({invitationId: this.invitationId}, formData).then((res: any) => {
      this.toastService.success(this.isVideoMode
        ? 'Uspesno ste izmenili video klipove za pozivnicu.'
        : 'Uspesno ste izmenili dizajn pozivnica.');
      this.isLoaderActive = false;
      this.formTriedToSubmit = false;
      this.confirmModal.emit(res.data);
    }).catch(() => {
      this.toastService.error('Doslo je do greske prilikom cuvanja pozivnice.');
      this.isLoaderActive = false;
    });
  }

  private patchMediaControl(): void {
    this.editInvitationForm.controls.media.patchValue(this.mediaItemsForEdit.length ? this.mediaItemsForEdit : null);
  }

  private patchDeletedMediaControl(): void {
    const controlName = this.isVideoMode ? 'deleted_videos_ids' : 'deleted_images_ids';
    this.editInvitationForm.controls[controlName].patchValue(this.deletedMediaIds);
  }

  private resetPositions(): void {
    this.mediaItemsForEdit = this.mediaItemsForEdit.map((item, index) => ({
      ...item,
      position: index + 1
    }));
  }

  private buildFormData(): FormData {
    const formData = new FormData();
    const mediaKey = this.isVideoMode ? 'videos' : 'images';
    const deletedMediaKey = this.isVideoMode ? 'deleted_videos_ids' : 'deleted_images_ids';

    this.mediaItemsForEdit.forEach((element: InvitationMediaItem, index: number) => {
      formData.append(`${mediaKey}[${index}][position]`, (index + 1).toString());
      if (element.id) {
        formData.append(`${mediaKey}[${index}][id]`, element.id.toString());
      } else if (element.file) {
        formData.append(`${mediaKey}[${index}][file]`, element.file);
      }
    });

    this.deletedMediaIds.forEach((id: number) => {
      formData.append(`${deletedMediaKey}[]`, id.toString());
    });

    return formData;
  }

  private addDeletedMediaId(id: number): void {
    if (!this.deletedMediaIds.includes(id)) {
      this.deletedMediaIds.push(id);
    }
  }

  private isAllowedFileType(file: File): boolean {
    if (this.isVideoMode) {
      return ['video/mp4', 'video/webm', 'video/quicktime'].includes(file.type);
    }

    return ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'].includes(file.type);
  }

  private currentMaxSize(): number {
    return this.isVideoMode ? this.videoMaxSize : this.imageMaxSize;
  }

  private getFileNameWithoutExtension(fileName: string): string {
    return fileName.replace(/\.[^/.]+$/, '');
  }
}
